<template>
    <div class="relative overflow-x-auto shadow-md sm:rounded-lg p-4 sm:ml-64 mt-16">
        <div class="flex items-center justify-between flex-wrap md:flex-row pb-4 bg-white dark:bg-gray-900">
            <!-- Date Picker -->
            <div id="date-range-picker" class="flex items-center">
                <div class="relative">
                    <input type="date" v-model="startDate"
                        class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5">
                </div>
                <span class="mx-4 text-gray-500">to</span>
                <div class="relative">
                    <input type="date" v-model="endDate"
                        class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5">
                </div>
                <button @click="fetchData" class="ml-2 bg-blue-500 text-white px-4 py-2 rounded-lg">Filter</button>
                <button @click="exportToExcel"
                    class="bg-emerald-500 text-white px-4 py-2 mx-2 rounded-lg hover:bg-emerald-700">
                    Export Excel
                </button>
            </div>
            <!-- Search Input -->
            <div class="relative">
                <input type="text" v-model="searchQuery" placeholder="Search for users"
                    class="block p-2 text-sm border rounded-lg w-80" />
            </div>
        </div>
        <!-- Table -->
        <table class="w-full text-sm text-left text-gray-500 dark:text-gray-400">
            <thead class="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700">
                <tr>
                    <th scope="col" class="px-6 py-3">Name</th>
                    <th scope="col" class="px-6 py-3 text-center">Email</th>
                    <th scope="col" class="px-6 py-3 text-center">Tanggal</th>
                    <th scope="col" class="px-6 py-3 text-center">Nama</th>
                    <th scope="col" class="px-6 py-3 text-center">NO REF</th>
                    <th scope="col" class="px-6 py-3 text-center">IDPEL</th>
                    <th scope="col" class="px-6 py-3 text-center">Type</th>
                    <th scope="col" class="px-6 py-3 text-center">Nominal</th>
                    <th scope="col" class="px-6 py-3 text-center">Admin Fee</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="(transaction) in filteredTransactions" :key="transaction.id"
                    class="bg-white border-b dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-600">
                    <td class="px-6 py-4">{{ transaction.username }}</td>
                    <td class="px-6 py-4">{{ transaction.email }}</td>
                    <td class="px-6 py-4">{{ transaction.formattedDate }}</td>
                    <td class="px-6 py-4">{{ transaction.nama }}</td>
                    <td class="px-6 py-4">{{ transaction.noRef }}</td>
                    <td class="px-6 py-4">{{ transaction.idpel ? transaction.idpel : '-' }}</td>
                    <td class="px-6 py-4">{{ transaction.type }}</td>
                    <td class="px-6 py-4">{{ transaction.nominal }}</td>
                    <td class="px-6 py-4">{{ transaction.admin }}</td>
                </tr>
            </tbody>
        </table>
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { getFirestore, collection, getDocs, query, where, Timestamp } from "firebase/firestore";
import * as XLSX from "xlsx";
import { format } from "date-fns";

const db = getFirestore();
const transactions = ref([]);
const searchQuery = ref("");
const startDate = ref("");
const endDate = ref("");
const fetchData = async () => {
    try {
        const usersSnapshot = await getDocs(collection(db, "users"));
        let allTransactions = [];

        for (const userDoc of usersSnapshot.docs) {
            const userData = userDoc.data();
            const username = userData.name || "";
            const email = userData.email || "";
            const uid = userData.uid;

            let billsQuery = query(collection(db, "bills"), where("uid", "==", uid));
            if (startDate.value && endDate.value) {
                const startTimestamp = Timestamp.fromDate(new Date(startDate.value));
                const endDateObj = new Date(endDate.value);
                endDateObj.setHours(23, 59, 59, 999);
                const endTimestamp = Timestamp.fromDate(endDateObj);
                billsQuery = query(billsQuery, where("timestamp", ">=", startTimestamp), where("timestamp", "<=", endTimestamp));
            }

            const billsSnapshot = await getDocs(billsQuery);
            billsSnapshot.docs.forEach(billDoc => {

                const billData = billDoc.data();
                //console.log(billData);
                const timestamp = billData.timestamp;
                const formattedDate = timestamp ? format(timestamp.toDate(), "d MMMM yyyy") : "";

                let nominal = "0";
                if (billData["RP BAYAR"]) {
                    nominal = billData["RP BAYAR"].toString();
                } else if (billData["RP TAG PLN"]) {
                    nominal = billData["RP TAG PLN"].toString();
                } else if (billData["TOTAL BAYAR"]) {
                    nominal = billData["TOTAL BAYAR"].toString();
                }

                allTransactions.push({
                    username,
                    email,
                    formattedDate,
                    nama: billData["NAMA"] || "",
                    noRef: billData["NO REF"] || "",
                    idpel: billData["IDPEL"] ? billData["IDPEL"] : "-",
                    type: billData["type"] || "",
                    nominal,
                    admin: billData["Admin Bank"] || "",
                });
            });
        }
        transactions.value = allTransactions;
    } catch (error) {
        console.error("Error fetching data:", error);
    }
};

const parseNominal = (value) => {
    if (!value) return 0; // Jika kosong, set ke 0
    return Number(value.replace(/[^\d]/g, "")); // Hapus non-digit dan konversi ke number
};

const exportToExcel = () => {
    const formattedTransactions = transactions.value.map(t => ({
        Name: t.username,
        Email: t.email,
        Tanggal: t.formattedDate,
        Nama: t.nama,
        "NO REF": t.noRef,
        IDPEL: t.idpel,
        Type: t.type,
        Nominal: parseNominal(t.nominal),  // Pastikan ini jadi angka
        "Admin Fee": parseNominal(t.admin) // Konversi admin fee ke angka jika ada
    }));

    // Hitung total nominal
    const totalNominal = formattedTransactions.reduce((sum, t) => sum + t.Nominal, 0);

    // Tambahkan baris total
    formattedTransactions.push({
        Name: "TOTAL",
        Email: "",
        Tanggal: "",
        Nama: "",
        "NO REF": "",
        IDPEL: "",
        Type: "",
        Nominal: totalNominal,  // Total sebagai angka
        "Admin Fee": ""
    });

    const ws = XLSX.utils.json_to_sheet(formattedTransactions);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Transactions");

    // Simpan file
    XLSX.writeFile(wb, `Agen SI MAMAT - ${format(new Date(), "yyyy-MM-dd_HH-mm-ss")}.xlsx`);
};

onMounted(fetchData);

const filteredTransactions = computed(() => {
    const query = searchQuery.value.toLowerCase(); // Konversi query ke lowercase
    return transactions.value.filter(transaction =>
        transaction.username.toLowerCase().includes(query) ||
        transaction.nama.toLowerCase().includes(query) ||
        transaction.idpel.toLowerCase().includes(query) ||
        transaction.noRef.toLowerCase().includes(query) ||
        transaction.type.toLowerCase().includes(query)
    );
});

</script>
